import { useTranslation } from "react-i18next";import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { AlertTriangle, Thermometer, Activity, Download, TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

export const ColdChainPanel = ({ temperatureProfile, compact = false }) => {const { t } = useTranslation("ezparcel");
  if (!temperatureProfile) return null;

  const { min, max, unit, sensorId, lastReading, lastUpdate, excursions, readings } = temperatureProfile;

  const isInRange = lastReading >= min && lastReading <= max;
  const hasExcursions = excursions && excursions.length > 0;

  const chartData = readings.map((r) => ({
    time: r.time,
    temp: r.temp,
    min: min,
    max: max
  }));

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 border border-border rounded-lg bg-card">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
        isInRange ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`
        }>
          <Thermometer className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{t("coldChain", "Cold Chain")}</span>
            <Badge className={`status-badge ${isInRange ? 'status-completed' : 'priority-high'}`}>
              {isInRange ? 'In Range' : 'Excursion'}
            </Badge>
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
            <span className="font-medium">{lastReading}{unit}</span>
            <span>{t("range", "Range:")}{min}–{max}{unit}</span>
            {hasExcursions &&
            <span className="flex items-center gap-1 text-red-600">
                <AlertTriangle className="h-3 w-3" />
                {excursions.length}{t("excursion", "excursion")}{excursions.length > 1 ? 's' : ''}
              </span>
            }
          </div>
        </div>
      </div>);

  }

  return (
    <Card className="card-elevated">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
            isInRange ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`
            }>
              <Thermometer className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{t("coldChainMonitoring", "Cold Chain Monitoring")}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">{t("sensor", "Sensor:")}
                {sensorId}{t("lastUpdate", "\u2022 Last update:")}{new Date(lastUpdate).toLocaleTimeString()}
              </p>
            </div>
          </div>
          <Badge className={`status-badge ${isInRange ? 'status-completed' : 'priority-high'}`}>
            {isInRange ? 'In Range' : 'Excursion Alert'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Status */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 border border-border rounded-lg">
            <p className="text-sm text-muted-foreground">{t("currentTemperature", "Current Temperature")}</p>
            <div className="flex items-baseline gap-2 mt-2">
              <p className="text-3xl font-bold">{lastReading}</p>
              <p className="text-lg text-muted-foreground">{unit}</p>
            </div>
            <div className="flex items-center gap-1 mt-2">
              {isInRange ?
              <>
                  <TrendingDown className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-600">{t("withinRange", "Within Range")}</span>
                </> :

              <>
                  <TrendingUp className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-600">{t("outOfRange", "Out of Range")}</span>
                </>
              }
            </div>
          </div>

          <div className="p-4 border border-border rounded-lg">
            <p className="text-sm text-muted-foreground">{t("requiredRange", "Required Range")}</p>
            <div className="flex items-baseline gap-2 mt-2">
              <p className="text-2xl font-bold">{min} – {max}</p>
              <p className="text-lg text-muted-foreground">{unit}</p>
            </div>
            <p className="text-sm text-muted-foreground mt-2">{t("complianceRequired", "Compliance Required")}</p>
          </div>

          <div className="p-4 border border-border rounded-lg">
            <p className="text-sm text-muted-foreground">{t("excursionsDetected", "Excursions Detected")}</p>
            <div className="flex items-baseline gap-2 mt-2">
              <p className="text-3xl font-bold">{excursions.length}</p>
            </div>
            {hasExcursions ?
            <div className="flex items-center gap-1 mt-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-red-600">{t("attentionRequired", "Attention Required")}</span>
              </div> :

            <span className="text-sm text-green-600 mt-2 block">{t("noIssues", "No Issues")}</span>
            }
          </div>
        </div>

        {/* Temperature Chart */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium">{t("temperatureTrendLast", "Temperature Trend (Last 4 Hours)")}</h4>
            <Button variant="outline" size="sm">
              <Activity className="h-4 w-4 mr-2" />{t("liveView", "Live View")}

            </Button>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="time"
                stroke="hsl(var(--muted-foreground))"
                style={{ fontSize: '12px' }} />
              
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                style={{ fontSize: '12px' }}
                domain={[min - 2, max + 2]} />
              
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }} />
              
              <ReferenceLine y={min} stroke="hsl(var(--info))" strokeDasharray="3 3" label="Min" />
              <ReferenceLine y={max} stroke="hsl(var(--info))" strokeDasharray="3 3" label="Max" />
              <Line
                type="monotone"
                dataKey="temp"
                stroke={isInRange ? 'hsl(var(--success))' : 'hsl(var(--destructive))'}
                strokeWidth={2}
                dot={{ fill: isInRange ? 'hsl(var(--success))' : 'hsl(var(--destructive))' }} />
              
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Excursions History */}
        {hasExcursions &&
        <div>
            <h4 className="text-sm font-medium mb-3">{t("excursionHistory", "Excursion History")}</h4>
            <div className="space-y-2">
              {excursions.map((excursion, idx) =>
            <div key={idx} className="flex items-start gap-3 p-3 border border-red-200 bg-red-50 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-sm">{t("temperatureExcursion", "Temperature Excursion:")}
                    {excursion.reading}{unit}
                      </p>
                      <Badge variant="outline" className="border-red-500 text-red-700">
                        {excursion.severity.toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {new Date(excursion.timestamp).toLocaleString()}{t("duration", "\u2022 Duration:")}{excursion.duration}{t("minutes", "minutes")}
                </p>
                    <p className="text-sm mt-2">
                      <span className="font-medium">{t("actionTaken", "Action Taken:")}</span> {excursion.action}
                    </p>
                  </div>
                </div>
            )}
            </div>
          </div>
        }

        {/* Compliance Actions */}
        <div className="flex gap-3 pt-4 border-t border-border">
          <Button variant="outline" className="flex-1">
            <Download className="h-4 w-4 mr-2" />{t("exportComplianceReport", "Export Compliance Report")}

          </Button>
          <Button variant="outline" className="flex-1">
            <Activity className="h-4 w-4 mr-2" />{t("fullTemperatureLog", "Full Temperature Log")}

          </Button>
        </div>
      </CardContent>
    </Card>);

};